import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { ApiForm, Field, TextInput, useApiForm } from '../../shared/form';
import { Button } from '../../shared/ui/button';
import { Modal } from '../../shared/ui/modal';
import {
  CheckResult,
  credentialKeys,
  replaceTitle,
  secretWordOf,
  updateCredential,
  type ICredentialApp,
  type TCredential,
  type TCredentialSaved,
} from '../../entities/credential';

const schema = z.object({ secret: z.string().min(1, 'Required') });
type TReplaceValues = z.infer<typeof schema>;

interface IReplaceSecretDialogProps {
  credential: TCredential;
  app: ICredentialApp;
  onOpenChange: (open: boolean) => void;
  onFailed: (saved: TCredentialSaved) => void;
}

/** Same id, so every step that names it keeps working; the server checks it on the way in. */
export const ReplaceSecretDialog = ({
  credential,
  app,
  onOpenChange,
  onFailed,
}: IReplaceSecretDialogProps) => {
  const client = useQueryClient();
  const replace = useMutation({
    mutationFn: (secret: string) =>
      updateCredential(credential.id, { fields: { [app.secretPath]: secret } }),
  });

  const form = useApiForm<TReplaceValues>({
    schema,
    defaultValues: { secret: '' },
    onSubmit: (values) =>
      replace.mutateAsync(values.secret, {
        onSuccess: async (saved) => {
          await client.invalidateQueries({ queryKey: credentialKeys.all });
          if (saved.check && !saved.check.ok) {
            onOpenChange(false);
            onFailed(saved);
          }
        },
      }),
  });

  const word = secretWordOf(app, credential.variant);
  const done = replace.data?.check?.ok === true;

  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      width="md"
      title={replaceTitle(app, credential.variant)}
      description={`Only the ${word} changes. Steps keep using these credentials.`}
      submit={
        done
          ? undefined
          : {
              label: 'Replace',
              onSubmit: () => void form.submit(),
              waiting: form.form.formState.isSubmitting,
              waitingLabel: 'Checking',
            }
      }
      footer={
        done ? (
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        ) : undefined
      }
    >
      <ApiForm api={form} flush>
        <Field<TReplaceValues> name="secret" label={`New ${word}`}>
          <TextInput
            mono
            autoFocus
            type="password"
            autoComplete="new-password"
            spellCheck={false}
            disabled={done}
          />
        </Field>
        {replace.data?.check && (
          <CheckResult ok={replace.data.check.ok} detail={replace.data.check.detail} />
        )}
        <button type="submit" hidden />
      </ApiForm>
    </Modal>
  );
};
